import React, { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTheme } from '../lib/theme';
import { getTaxSettings, patchTaxSettings } from '../lib/api';

const PROVINCES = [
  ['QC', 'Quebec'], ['ON', 'Ontario'], ['BC', 'British Columbia'], ['AB', 'Alberta'],
  ['MB', 'Manitoba'], ['SK', 'Saskatchewan'],
  ['NB', 'New Brunswick'], ['NS', 'Nova Scotia'], ['PE', 'Prince Edward Island'],
  ['NL', 'Newfoundland and Labrador'],
  ['YT', 'Yukon'], ['NT', 'Northwest Territories'], ['NU', 'Nunavut'],
];

const PROVINCIAL_FILERS = new Set(['QC', 'BC', 'SK', 'MB']);

const FREQ_OPTIONS = [
  ['monthly',        'Monthly'],
  ['quarterly',      'Quarterly'],
  ['annually',       'Annually'],
  ['not_applicable', 'Not applicable'],
];

const STRUCTURE_OPTIONS = [
  ['sole_prop_or_partnership', 'Sole Proprietorship or Partnership'],
  ['corporation',              'Corporation'],
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthDayFromMMDD(mmdd) {
  if (!mmdd) return { m: 12, d: 31 };
  return { m: parseInt(mmdd.slice(0, 2), 10) || 12, d: parseInt(mmdd.slice(3), 10) || 31 };
}
function toMMDD(m, d) {
  return `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function daysInMonth(m) {
  // leap-safe: use 2024
  return new Date(2024, m, 0).getDate();
}

export default function TaxSettings() {
  const { colors } = useTheme();
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await getTaxSettings();
      setSettings(s);
    } catch (e) {
      setError(e?.detail || 'Failed to load');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const persist = async (patch) => {
    setError(null);
    try {
      const next = await patchTaxSettings(patch);
      setSettings(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e?.detail || 'Save failed');
    }
  };

  if (!settings) return null;

  const provincialAllowed = settings.province && PROVINCIAL_FILERS.has(settings.province);
  const { m, d } = monthDayFromMMDD(settings.year_end_date);
  const maxDay = daysInMonth(m);
  const currentFreq = settings.tax_declaration_frequency || (provincialAllowed ? '' : 'not_applicable');

  return (
    <div className="rounded-xl border overflow-hidden divide-y"
      data-testid="tax-settings"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
      {/* Province */}
      <div className="px-5 py-3.5">
        <label className="text-xs font-semibold block mb-1" style={{ color: colors.textMuted }}>Province of Business</label>
        <div className="relative">
          <select
            data-testid="tax-province"
            value={settings.province || ''}
            onChange={(e) => {
              const p = e.target.value || null;
              const nextPatch = { province: p };
              if (p && !PROVINCIAL_FILERS.has(p)) {
                nextPatch.tax_declaration_frequency = 'not_applicable';
              }
              persist(nextPatch);
            }}
            className="w-full appearance-none px-3 py-2 pr-8 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
            <option value="">Select…</option>
            {PROVINCES.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: colors.textMuted }} />
        </div>
      </div>

      {/* Business Structure */}
      <div className="px-5 py-3.5">
        <label className="text-xs font-semibold block mb-1" style={{ color: colors.textMuted }}>Business Structure</label>
        <div className="relative">
          <select
            data-testid="tax-business-structure"
            value={settings.business_structure || ''}
            onChange={(e) => persist({ business_structure: e.target.value || null })}
            className="w-full appearance-none px-3 py-2 pr-8 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
            <option value="">Select…</option>
            {STRUCTURE_OPTIONS.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: colors.textMuted }} />
        </div>
      </div>

      {/* Year-End Date */}
      <div className="px-5 py-3.5">
        <label className="text-xs font-semibold block mb-1" style={{ color: colors.textMuted }}>Year-End Date</label>
        <div className="flex gap-2">
          <select
            data-testid="tax-year-end-month"
            value={settings.year_end_date ? m : ''}
            onChange={(e) => {
              const mm = parseInt(e.target.value, 10);
              const dd = Math.min(d, daysInMonth(mm));
              persist({ year_end_date: toMMDD(mm, dd) });
            }}
            className="flex-1 appearance-none px-3 py-2 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
            <option value="">Month…</option>
            {MONTHS.map((mn, i) => <option key={mn} value={i + 1}>{mn}</option>)}
          </select>
          <select
            data-testid="tax-year-end-day"
            value={settings.year_end_date ? d : ''}
            disabled={!settings.year_end_date}
            onChange={(e) => {
              const dd = parseInt(e.target.value, 10);
              persist({ year_end_date: toMMDD(m, dd) });
            }}
            className="w-24 appearance-none px-3 py-2 rounded-lg text-sm outline-none border"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
            <option value="">Day…</option>
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((day) => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Frequency */}
      <div className="px-5 py-3.5">
        <label className="text-xs font-semibold block mb-1" style={{ color: colors.textMuted }}>Tax Declaration Frequency</label>
        <div className="relative">
          <select
            data-testid="tax-frequency"
            value={currentFreq}
            disabled={!provincialAllowed && !!settings.province}
            onChange={(e) => persist({ tax_declaration_frequency: e.target.value })}
            className="w-full appearance-none px-3 py-2 pr-8 rounded-lg text-sm outline-none border disabled:opacity-60"
            style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
            <option value="">Select…</option>
            {FREQ_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: colors.textMuted }} />
        </div>
        {settings.province && !provincialAllowed ? (
          <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
            Only QC / BC / SK / MB have provincial tax filing
          </p>
        ) : null}
      </div>

      {saved || error ? (
        <div className="px-5 py-2 flex items-center gap-2 text-xs"
          data-testid="tax-settings-toast"
          style={{ color: error ? colors.error : colors.success, backgroundColor: colors.bg }}>
          {error
            ? <span>{error}</span>
            : <><Check size={12} /> <span>Saved</span></>}
        </div>
      ) : null}
    </div>
  );
}
